/**
 * V13.19 — Focus Jeux & Mini-Jeux du Pacifique (vues publiques historiques)
 *
 * Composant à intégrer en tête de /memoire (sous l'article à la une).
 * Liste les éditions importées (Jeux + Mini-Jeux) avec stats médailles PF et lien
 * vers la page détail de chaque édition (tableau filtrable des résultats).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Flag, MapPin, Calendar, ChevronRight, Archive } from "lucide-react";

const TYPE_LABEL = {
  pacific_games: "Jeux du Pacifique",
  pacific_mini_games: "Mini-Jeux du Pacifique",
};

function MedalPill({ label, value, tone }) {
  const cls =
    tone === "or"
      ? "text-[hsl(var(--aito-gold))]"
      : tone === "argent"
      ? "text-slate-500 dark:text-slate-300"
      : tone === "bronze"
      ? "text-amber-700 dark:text-amber-500"
      : "text-[hsl(var(--aito-lagoon))]";
  return (
    <div
      className="flex flex-col items-center leading-none"
      data-testid={`focus-jeux-medal-${tone}`}
    >
      <span className={`font-mono font-bold text-base sm:text-lg ${cls}`}>{value}</span>
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground mt-0.5">
        {label}
      </span>
    </div>
  );
}

function EditionCard({ ed }) {
  const stats = ed.stats_pf || { or: 0, argent: 0, bronze: 0, total: 0 };
  const results = ed.results_count || 0;
  const host =
    [ed.host_city, ed.host_country].filter(Boolean).join(", ") || "Lieu inconnu";
  return (
    <Link
      to={`/memoire/jeux-du-pacifique/${ed.key}`}
      data-testid={`focus-jeux-card-${ed.key}`}
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-lagoon))] rounded-xl"
    >
      <Card className="p-4 sm:p-5 h-full border-slate-200 hover:border-[hsl(var(--aito-lagoon))]/40 hover:shadow-md transition-[box-shadow,border-color,transform] duration-200 group-hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge
            className={
              ed.type === "pacific_games"
                ? "bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30"
                : "bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold-strong,var(--aito-gold)))] border-[hsl(var(--aito-gold))]/40"
            }
          >
            {TYPE_LABEL[ed.type] || "Édition"}
          </Badge>
          <span className="font-mono text-xs text-muted-foreground shrink-0">
            <Calendar className="inline h-3 w-3 mr-0.5 -mt-0.5" />
            {ed.year}
          </span>
        </div>
        <h3 className="font-display font-bold text-base sm:text-lg leading-tight mb-1 line-clamp-2">
          {ed.name}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{host}</span>
        </div>

        <div className="flex items-center gap-3 mb-3 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
            <Flag className="h-3 w-3 text-[hsl(var(--aito-gold))]" />
            Polynésie
          </div>
          <div className="grid grid-cols-4 gap-x-2.5 flex-1">
            <MedalPill label="Or" value={stats.or} tone="or" />
            <MedalPill label="Argent" value={stats.argent} tone="argent" />
            <MedalPill label="Bronze" value={stats.bronze} tone="bronze" />
            <MedalPill label="Total" value={stats.total} tone="total" />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Archive className="h-3.5 w-3.5" />
            {results.toLocaleString("fr-FR")} résultat{results > 1 ? "s" : ""}
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-[hsl(var(--aito-lagoon))] group-hover:gap-1.5 transition-all">
            Explorer
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Card>
    </Link>
  );
}

function CardSkeleton() {
  return (
    <Card className="p-5">
      <Skeleton className="h-4 w-20 mb-2" />
      <Skeleton className="h-5 w-3/4 mb-2" />
      <Skeleton className="h-3 w-1/2 mb-4" />
      <Skeleton className="h-10 w-full" />
    </Card>
  );
}

export function FocusJeuxPacifique() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/games/editions")
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Le composant est monté quel que soit l'état ; masqué s'il n'y a strictement rien à afficher
  // en fin de chargement (évite une section vide inutile pour un visiteur avant tout import).
  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <section
      id="focus-jeux-pacifique"
      className="pt-10 scroll-mt-24"
      data-testid="section-focus-jeux-pacifique"
    >
      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30 font-semibold">
            <Trophy className="h-3 w-3 mr-1" />
            Focus
          </Badge>
          <h2 className="font-display font-bold text-2xl sm:text-3xl">
            Jeux &amp; Mini-Jeux du Pacifique
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          L'archive historique — éditions passées, résultats de la Polynésie et de toutes les
          délégations, importés depuis les sources officielles.
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="focus-jeux-grid"
      >
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
          : items.map((ed) => <EditionCard key={ed.id || ed.key} ed={ed} />)}
      </div>

      {/* V13.21 (Itération E) — CTA vers le reporting cross-éditions */}
      {!loading && items.length > 0 && (
        <div className="mt-4 text-center">
          <Link
            to="/reporting/jeux-du-pacifique"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
            data-testid="focus-jeux-reporting-link"
          >
            📊 Voir le reporting cross-éditions (Tahiti, top délégations, disciplines phares)
          </Link>
        </div>
      )}
    </section>
  );
}

export default FocusJeuxPacifique;
